import type { Annotation } from "building-types/annotation/annotationTypes";
import { AnnotationType, PositioningMethod } from "building-types/annotation/annotationTypesEnum";
import { WallHorizontalDatum } from "building-types/datums/datumsTypesEnum";
import type {
  AnnotationHostEntity,
  AnnotationHostWall,
  EntityHostedAnnotationPoint,
  SnapHostEntity,
} from "building-types/entities/entityHostedAnnotationTypes";
import { WallHostedAnnotationSide } from "building-types/entities/entityHostedAnnotationTypes";
import type { RoomHandle } from "building-types/handles/docEntityHandleTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import type { FeatureSet } from "common/constants/features";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { line2Tangent } from "geom/line2";
import { polyLine2EdgeEval, polyLine2EdgeTangent } from "geom/polyLine2";
import { vec2Dot, vec2DistSquared } from "geom/vec2";

export function annotationHostWallNew(
  handle: RoomHandle,
  edgeIndex: number,
  param: number = 0,
  paramNormalized: boolean = false,
  wallSide: WallHostedAnnotationSide = WallHostedAnnotationSide.OUTSIDE,
  horizontalDatum: WallHorizontalDatum = WallHorizontalDatum.ROOM_EDGE,
): AnnotationHostWall {
  return {
    type: "WALL",
    handle,
    edgeIndex,
    param,
    paramNormalized,
    wallSide,
    horizontalDatum,
  };
}

export function entityHostedAnnotationPointNew(
  hostEntity: AnnotationHostEntity | undefined,
  enabled: boolean = false,
): EntityHostedAnnotationPoint {
  return {
    hostEntity,
    enabled,
  };
}

interface ScoredCandidate {
  readonly mainScore: number;
  readonly secondaryScore: number;
  readonly candidate: SnapHostEntity;
}

export function getDimensionHostEntitySelector(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
): (candidates: SnapHostEntity[]) => SnapHostEntity | undefined {
  const [dimensionTx, dimensionTy] = line2Tangent(startX, startY, endX, endY);

  const hostEntitySelector = (candidates: SnapHostEntity[]): SnapHostEntity | undefined => {
    let scoredCandidates: ScoredCandidate[] = candidates.map((candidate) => {
      const { outline, edgeIndex, param, paramNormalized } = candidate;
      const [x, y] = polyLine2EdgeEval(outline, edgeIndex, param, paramNormalized);
      const isStart = vec2DistSquared(startX, startY, x, y) < DIST_EPSILON_SQUARED;
      const oppX = isStart ? endX : startX;
      const oppY = isStart ? endY : startY;
      const [tx, ty] = polyLine2EdgeTangent(outline, edgeIndex);
      const [nextTx, nextTy] = polyLine2EdgeTangent(outline, edgeIndex + 1);

      return {
        mainScore: Math.abs(vec2Dot(dimensionTx, dimensionTy, tx, ty)),
        secondaryScore: vec2DistSquared(oppX, oppY, x + nextTx, y + nextTy),
        candidate,
      };
    });

    scoredCandidates = scoredCandidates.sort((a: ScoredCandidate, b: ScoredCandidate): number => {
      const mainScoreDiff = a.mainScore - b.mainScore;
      if (Math.abs(mainScoreDiff) > DIST_EPSILON) {
        return mainScoreDiff;
      }

      return a.secondaryScore - b.secondaryScore;
    });

    return scoredCandidates.length > 0 ? scoredCandidates[0].candidate : undefined;
  };

  return hostEntitySelector;
}

function selectFirstHostEntity(candidates: SnapHostEntity[]): SnapHostEntity | undefined {
  return candidates.length > 0 ? candidates[0] : undefined;
}

export function getAnnotationHostEntity(
  snapHostEntities: SnapHostEntity[],
  selector: (candidates: SnapHostEntity[]) => SnapHostEntity | undefined = selectFirstHostEntity,
): AnnotationHostEntity | undefined {
  const bestCandidate = selector(snapHostEntities);

  if (!bestCandidate) {
    return undefined;
  }

  switch (bestCandidate.handle.type) {
    case "ROOM":
      return annotationHostWallNew(
        bestCandidate.handle as RoomHandle,
        bestCandidate.edgeIndex,
        bestCandidate.param,
        bestCandidate.paramNormalized,
      );
    default:
      return undefined;
  }
}

export function getEntityHostedAnnotationPoint(
  enabledFeatures: FeatureSet | undefined,
  snapHostEntities: SnapHostEntity[],
  hostEntitySelector: (candidates: SnapHostEntity[]) => SnapHostEntity | undefined = selectFirstHostEntity,
  enableEntityHostedPoint: boolean | undefined,
): EntityHostedAnnotationPoint | undefined {
  if (!enabledFeatures?.entityHostedAnnotations) {
    return undefined;
  }

  const hostEntity = getAnnotationHostEntity(snapHostEntities, hostEntitySelector);

  return entityHostedAnnotationPointNew(hostEntity, enableEntityHostedPoint);
}

export function isEntityHostedAnnotationPoint(
  entityHostedPoint: EntityHostedAnnotationPoint | undefined,
): entityHostedPoint is { hostEntity: AnnotationHostEntity } {
  return !!entityHostedPoint?.enabled && !!entityHostedPoint?.hostEntity;
}

export function isEntityHostedAnnotation(anno: Annotation): boolean {
  switch (anno?.type) {
    case AnnotationType.LINEAR_DIMENSION:
      const { entityHostedStart, entityHostedEnd } = anno;
      return isEntityHostedAnnotationPoint(entityHostedStart) || isEntityHostedAnnotationPoint(entityHostedEnd);
    default:
      // TODO: handle other annotation types
      return false;
  }
}

export function transformEntityHostedToEdge(annotation: Annotation, host: Room | StaticRoom | null): Annotation {
  if (host?.edgeIds === undefined || !annotation.edgeId) {
    return annotation;
  }
  const edgeId = annotation.edgeId;

  const edgeIndex = host.edgeIds.indexOf(edgeId);

  if (edgeIndex < 0) {
    return annotation;
  }

  const startIndex = edgeIndex * 2;
  const originX = host.outline[startIndex];
  const originY = host.outline[startIndex + 1];

  switch (annotation?.type) {
    case AnnotationType.LINEAR_DIMENSION: {
      let { startX, startY, endX, endY, lineStartX, lineStartY, lineEndX, lineEndY } = annotation;
      startX -= originX;
      startY -= originY;
      endX -= originX;
      endY -= originY;
      lineStartX -= originX;
      lineStartY -= originY;
      lineEndX -= originX;
      lineEndY -= originY;
      return {
        ...annotation,
        startX,
        startY,
        endX,
        endY,
        lineStartX,
        lineStartY,
        lineEndX,
        lineEndY,
        entityHostedStart: undefined,
        entityHostedEnd: undefined,
        positioningMethod: PositioningMethod.EDGE,
      };
    }
    default:
      return annotation;
  }
}
